import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  BuildingsIcon,
  ReceiptIcon,
  IdentificationCardIcon,
  WarningDiamondIcon,
  SealCheckIcon,
  ClockCountdownIcon,
  ArrowRightIcon,
} from "@phosphor-icons/react";

const ENTITY_TYPES = [
  { id: "sole_proprietorship", label: "Sole Proprietorship" },
  { id: "llc", label: "LLC" },
  { id: "s_corporation", label: "S-Corporation" },
  { id: "c_corporation", label: "C-Corporation" },
  { id: "partnership", label: "Partnership" },
  { id: "nonprofit", label: "Nonprofit" },
  { id: "other", label: "Other" },
];

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut",
  "Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa",
  "Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan",
  "Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire",
  "New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma",
  "Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee",
  "Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming",
  "District of Columbia",
];

export default function WholesaleCredentials() {
  const { user, loading: authLoading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("unsubmitted");
  const [rejectionReason, setRejectionReason] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    business_name: "",
    business_entity_type: "llc",
    business_state: "",
    tin: "",
    resale_permit_number: "",
    resale_permit_state: "",
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    if (user.account_type !== "wholesale_buyer") {
      navigate("/dashboard");
      return;
    }
    api
      .get("/me/wholesale-credentials")
      .then((r) => {
        setStatus(r.data.status || "unsubmitted");
        setRejectionReason(r.data.rejection_reason);
        setCredentials(r.data.credentials);
        if (r.data.credentials) {
          setForm((prev) => ({
            ...prev,
            business_name: r.data.credentials.business_name || "",
            business_entity_type: r.data.credentials.business_entity_type || "llc",
            business_state: r.data.credentials.business_state || "",
            resale_permit_number: r.data.credentials.resale_permit_number || "",
            resale_permit_state: r.data.credentials.resale_permit_state || "",
          }));
        }
      })
      .finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/me/wholesale-credentials", form);
      toast.success("Submitted. We'll review within 24h.");
      setStatus("pending");
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) return null;
  if (!user || user.account_type !== "wholesale_buyer") return null;

  const readOnly = status === "approved" || status === "pending";

  return (
    <div className="min-h-screen bg-[#F6F4EE] flex flex-col" data-testid="wholesale-credentials-page">
      <Navbar />
      <main className="flex-1 max-w-4xl mx-auto w-full px-6 py-12">
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
          Wholesale buyer credentials
        </h1>
        <p className="text-sm text-zinc-600 mt-3 max-w-2xl">
          Only qualified customers that apply to buy from wholesalers can purchase from wholesalers
          on Bidorah. Provide the information below — <b>no exceptions</b>. Approvals typically land
          within 24 hours.
        </p>

        {/* Status banner */}
        {status === "approved" && (
          <div
            className="mt-8 rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-5 flex items-center gap-3"
            data-testid="status-banner-approved"
          >
            <SealCheckIcon size={28} weight="fill" className="text-emerald-600" />
            <div>
              <p className="font-display font-bold text-emerald-900">Approved — you're good to buy.</p>
              <p className="text-sm text-emerald-900/80">
                Head to the wholesale marketplace whenever you're ready.
              </p>
            </div>
            <Button
              className="ml-auto bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
              onClick={() => navigate("/browse?segment=wholesale")}
            >
              Browse wholesale <ArrowRightIcon size={14} className="ml-1" />
            </Button>
          </div>
        )}
        {status === "pending" && (
          <div
            className="mt-8 rounded-2xl border-2 border-amber-400 bg-amber-50 p-5 flex items-center gap-3"
            data-testid="status-banner-pending"
          >
            <ClockCountdownIcon size={28} className="text-amber-600" />
            <div>
              <p className="font-display font-bold text-amber-900">Under review.</p>
              <p className="text-sm text-amber-900/80">
                We've received your credentials. Wholesale bidding and checkout are blocked until
                approval. You'll get an in-app notification when it's done.
              </p>
            </div>
          </div>
        )}
        {status === "rejected" && (
          <div
            className="mt-8 rounded-2xl border-2 border-red-400 bg-red-50 p-5 flex items-center gap-3"
            data-testid="status-banner-rejected"
          >
            <WarningDiamondIcon size={28} weight="fill" className="text-red-600" />
            <div>
              <p className="font-display font-bold text-red-900">
                Your application was not approved.
              </p>
              <p className="text-sm text-red-900/80">
                {rejectionReason ||
                  "We couldn't verify one or more of your documents. Update the form below and re-submit."}
              </p>
            </div>
          </div>
        )}

        {/* Requirement cards */}
        <div className="mt-8 grid md:grid-cols-3 gap-3">
          <div className="card-modern p-5">
            <div className="flex items-center gap-2 mb-2">
              <ReceiptIcon size={18} className="text-[#1D78AB]" />
              <p className="font-display font-bold">Reseller's Permit</p>
            </div>
            <p className="text-xs text-zinc-600 leading-relaxed">
              Lets you buy from wholesalers without paying sales tax. Issued by your state's tax
              authority.
            </p>
          </div>
          <div className="card-modern p-5">
            <div className="flex items-center gap-2 mb-2">
              <IdentificationCardIcon size={18} className="text-[#1D78AB]" />
              <p className="font-display font-bold">Taxpayer ID (TIN)</p>
            </div>
            <p className="text-xs text-zinc-600 leading-relaxed">
              Required for tax purposes — especially when collecting sales tax from customers. EIN
              or SSN. Stored encrypted; only last-4 is visible.
            </p>
          </div>
          <div className="card-modern p-5">
            <div className="flex items-center gap-2 mb-2">
              <BuildingsIcon size={18} className="text-[#1D78AB]" />
              <p className="font-display font-bold">Registered Business</p>
            </div>
            <p className="text-xs text-zinc-600 leading-relaxed">
              Sole proprietorship, LLC, S/C-Corp, partnership, or nonprofit — registered with your
              state.
            </p>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={submit}
          className="mt-8 card-modern p-6 lg:p-8 space-y-5"
          data-testid="wholesale-credentials-form"
        >
          <p className="overline text-zinc-500">YOUR DETAILS</p>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="overline text-zinc-500 block mb-1">Registered business name *</label>
              <Input
                value={form.business_name}
                onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                placeholder="e.g. Leo's Liquidations LLC"
                className="rounded-full h-11 px-4"
                required
                disabled={readOnly}
                data-testid="wc-business-name"
              />
            </div>
            <div>
              <label className="overline text-zinc-500 block mb-1">Entity type *</label>
              <Select
                value={form.business_entity_type}
                onValueChange={(v) => setForm({ ...form, business_entity_type: v })}
                disabled={readOnly}
              >
                <SelectTrigger className="bg-white" data-testid="wc-entity-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENTITY_TYPES.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="overline text-zinc-500 block mb-1">Business registered in (state) *</label>
              <Select
                value={form.business_state}
                onValueChange={(v) => setForm({ ...form, business_state: v })}
                disabled={readOnly}
              >
                <SelectTrigger className="bg-white" data-testid="wc-business-state">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {US_STATES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="overline text-zinc-500 block mb-1">Taxpayer ID (EIN or SSN) *</label>
              <Input
                value={form.tin}
                onChange={(e) => setForm({ ...form, tin: e.target.value })}
                placeholder="12-3456789 or 123-45-6789"
                className="rounded-full h-11 px-4 font-mono"
                required={!readOnly}
                disabled={readOnly}
                data-testid="wc-tin"
              />
              {readOnly && credentials?.tin_last4 && (
                <p className="text-[11px] text-zinc-500 mt-1">On file — ending in {credentials.tin_last4}</p>
              )}
            </div>
            <div>
              <label className="overline text-zinc-500 block mb-1">Reseller's permit number *</label>
              <Input
                value={form.resale_permit_number}
                onChange={(e) => setForm({ ...form, resale_permit_number: e.target.value })}
                placeholder="e.g. SR-12-345678"
                className="rounded-full h-11 px-4 font-mono"
                required
                disabled={readOnly}
                data-testid="wc-permit-number"
              />
            </div>
            <div>
              <label className="overline text-zinc-500 block mb-1">Permit issuing state *</label>
              <Select
                value={form.resale_permit_state}
                onValueChange={(v) => setForm({ ...form, resale_permit_state: v })}
                disabled={readOnly}
              >
                <SelectTrigger className="bg-white" data-testid="wc-permit-state">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {US_STATES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="pt-4 border-t border-zinc-100 flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs text-zinc-500 max-w-md">
              Submitting false or invalid credentials results in a permanent ban and, where
              applicable, report to state tax authorities.
            </p>
            {!readOnly ? (
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#1D78AB] hover:bg-[#165d85] text-white h-11 px-7 rounded-full font-semibold shadow-lg shadow-[#1D78AB]/20"
                data-testid="wc-submit-btn"
              >
                {saving
                  ? "Submitting…"
                  : status === "rejected"
                  ? "Re-submit for review"
                  : "Submit for review"}
              </Button>
            ) : (
              <p className="text-xs text-zinc-500">
                {status === "pending"
                  ? "Locked while under review."
                  : "Approved — contact support to update."}
              </p>
            )}
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
}
